import { useEffect, useMemo, useState } from 'react';

import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from '@firebase/firestore';

import { ICollection } from 'types/collection';
import { ISanitizedCompany } from 'types/company';
import { IStory } from 'types/story';

import { clientStoryConverter } from 'services/api/firestore/converters';
import { sortStoriesByCollectionPosition } from 'services/collection';

import { searchStories } from 'clients/algolia';
import { db } from 'clients/firebase';

import { useUserPermissions } from 'hooks/auth/user';
import { useMediaQuery } from 'hooks/browser/mediaQueries';
import { useFetchCollections } from 'hooks/collections/firebase/fetch';
import { useGetQueryFilters } from 'hooks/filters';
import { useGetSearchTerm } from 'hooks/search';
import { useFetchStories } from 'hooks/stories/firebase/fetch';

export const useGetCompany = (companySlug: string) => {
  const [company, setCompany] = useState<ISanitizedCompany | null>(null);
  const [companyNotFound, setCompanyNotFound] = useState(false);
  const [companyError, setCompanyError] = useState(false);
  const [prevCompanySlug, setPrevCompanySlug] = useState(companySlug);

  if (companySlug !== prevCompanySlug) {
    setPrevCompanySlug(companySlug);
    setCompany(null);
    setCompanyNotFound(false);
    setCompanyError(false);
  }

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch('/api/integration/companyBySlug', {
          method: 'POST',
          body: JSON.stringify({ companySlug }),
        });

        if (cancelled) {
          return;
        }

        if (response.status === 404) {
          setCompanyNotFound(true);
          return;
        }

        if (!response.ok) {
          setCompanyError(true);
          return;
        }

        const data = await response.json();
        if (!cancelled) {
          setCompany(data);
        }
      } catch {
        if (!cancelled) {
          setCompanyError(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companySlug]);

  return { company, companyNotFound, companyError };
};

export const useGetStories = (company: ISanitizedCompany) => {
  const { canCreateStories } = useUserPermissions(company);
  const { filtersState } = useGetQueryFilters();
  const { matches: isTablet } = useMediaQuery(
    '(min-width: 768px) and (max-width: 1023px)',
  );
  const { term } = useGetSearchTerm();

  const [searchResults, setSearchResults] = useState<IStory[] | null>(null);
  const searchInputs = {
    term,
    companyId: company.id,
    topics: filtersState?.allTopics,
  };
  const [prevSearchInputs, setPrevSearchInputs] = useState(searchInputs);

  if (
    searchInputs.term !== prevSearchInputs.term ||
    searchInputs.companyId !== prevSearchInputs.companyId ||
    searchInputs.topics !== prevSearchInputs.topics
  ) {
    setPrevSearchInputs(searchInputs);
    setSearchResults(null);
  }

  useEffect(() => {
    if (!term) {
      return;
    }

    let cancelled = false;

    (async () => {
      let results: IStory[];
      try {
        results = await searchStories({
          companyId: company.id,
          term,
          topicFilters: filtersState?.allTopics || [],
        });
      } catch {
        results = [];
      }

      if (!cancelled) {
        setSearchResults(results);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [term, company.id, filtersState?.allTopics]);

  const options = useMemo(() => {
    if (
      term ||
      typeof isTablet === 'undefined' ||
      (typeof canCreateStories === 'undefined' && company.private)
    ) {
      return null;
    }

    const itemsPerPage = isTablet ? 9 : 8;
    const initialItems = itemsPerPage - (canCreateStories ? 1 : 0);

    return {
      orderBy: ['meta.publishedAt', 'desc'] as [string, 'asc' | 'desc'],
      isPublished: true,
      isInFeed: true,
      topicFilters: filtersState?.allTopics || null,
      initialItems,
      itemsPerPage,
      inTeam: true,
    };
  }, [
    canCreateStories,
    company.private,
    filtersState?.allTopics,
    isTablet,
    term,
  ]);

  const {
    stories: firebaseStories,
    fetchMore,
    canFetchMore,
  } = useFetchStories(company, options);

  const stories = term ? searchResults : firebaseStories;

  return {
    stories,
    fetchMore,
    canFetchMore: term ? false : canFetchMore,
    term,
  };
};

export const useGetCollections = (company: ISanitizedCompany) => {
  const options = useMemo(() => {
    return {
      orderBy: ['meta.publishedAt', 'desc'] as [string, 'asc' | 'desc'],
      isPublished: true,
      showInFeed: true,
      inTeam: true,
    };
  }, []);

  const { collectionsWithStoryCounts } = useFetchCollections(company, options);

  return { collectionsWithStoryCounts };
};

export const useFetchStoriesInCollection = (collectionData: ICollection) => {
  const queryConstraints = useMemo(() => {
    const queryConstraints = [
      where('meta.collectionId', '==', collectionData.id),
      where('meta.companyId', '==', collectionData.meta.companyId),
      where('meta.publishedAt', '!=', null),
      orderBy('meta.publishedAt', 'desc'),
    ];

    return queryConstraints;
  }, [collectionData]);

  const [storiesInCollection, setStoriesInCollection] = useState<
    IStory[] | null
  >(null);

  useEffect(() => {
    (async () => {
      if (!queryConstraints) {
        return;
      }

      const baseQuery = query(collection(db, 'Stories'), ...queryConstraints);

      const { docs: storyDocs } = await getDocs(
        query(baseQuery).withConverter(clientStoryConverter),
      );

      const stories = storyDocs.map(doc => doc.data() as IStory);

      setStoriesInCollection(
        sortStoriesByCollectionPosition(stories, collectionData.position),
      );
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryConstraints]);

  return {
    storiesInCollection,
  };
};
